import * as Sentry from "@sentry/react";
import { appEnvironment } from "@/lib/appEnvironment";
import type { TokenPayload } from "@/types";

const sentryDsn = import.meta.env.VITE_GLITCHTIP_DSN?.trim();

function isAbortLike(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }
  if (error.name === "AbortError") {
    return true;
  }
  if ("code" in error && error.code === "ECONNABORTED") {
    return true;
  }
  return /abort/i.test(`${error.name} ${error.message}`);
}

function isClientHttpError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== "ApiClientError") {
    return false;
  }
  const status =
    "response" in error
      ? (error as { response?: { status?: number } }).response?.status
      : undefined;
  return typeof status === "number" && status < 500;
}

/** Safari clipboard denial — UX toast handles it. */
function isClipboardPermissionNoise(error: unknown): boolean {
  return error instanceof Error && error.name === "NotAllowedError";
}

function redactAccessTokens(event: Sentry.ErrorEvent): void {
  for (const crumb of event.breadcrumbs ?? []) {
    const url = crumb.data?.url;
    if (typeof url === "string" && crumb.data) {
      crumb.data.url = url.replace(
        /([?&]access_token=)[^&]+/gi,
        "$1[redacted]",
      );
    }
  }
}

function installConsoleExceptionCapture(): void {
  const originalError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    const err = args.find((arg): arg is Error => arg instanceof Error);
    if (err) {
      Sentry.captureException(err, { tags: { source: "console.error" } });
    }
    originalError(...args);
  };
}

let initialized = false;

export function initSentry(): void {
  if (initialized || !sentryDsn) {
    return;
  }
  initialized = true;
  Sentry.init({
    dsn: sentryDsn,
    enabled: !import.meta.env.DEV,
    environment: appEnvironment,
    release: `flitrip-frontend-seller@${import.meta.env.VITE_APP_VERSION}`,
    tracesSampleRate: 0.2,
    sendDefaultPii: false,
    attachStacktrace: true,
    ignoreErrors: [/abort/i],
    denyUrls: [/^chrome-extension:\/\//i, /^moz-extension:\/\//i],
    // GlitchTip does not support sessions; drop BrowserSession and keep tracing.
    integrations: (defaults) => [
      ...defaults.filter((integration) => integration.name !== "BrowserSession"),
      Sentry.browserTracingIntegration(),
    ],
    beforeSend(event, hint) {
      const error = hint.originalException;
      if (isAbortLike(error) || isClientHttpError(error) || isClipboardPermissionNoise(error)) {
        return null;
      }
      redactAccessTokens(event);
      return event;
    },
  });
  if (!import.meta.env.DEV) {
    installConsoleExceptionCapture();
  }
}

export function setSentryUser(user: TokenPayload | null): void {
  if (!sentryDsn) {
    return;
  }
  if (user === null) {
    Sentry.setUser(null);
    Sentry.getCurrentScope().setTag("user.role", undefined);
    Sentry.getCurrentScope().setTag("impersonating", undefined);
    return;
  }
  Sentry.setUser({ id: user.sub });
  Sentry.setTag("user.role", user.role);
  Sentry.setTag("impersonating", user.impersonator_sub ? "true" : "false");
}
